import { type FC, useCallback, useMemo, useRef, useState } from "react";

import type { UsageTimeseriesBucket } from "../../types/usage";

import { DARK_THEME, LIGHT_THEME } from "../../theme/theme-constants";
import {
  formatTokenCount,
  formatUsd,
  isFiniteNumber,
} from "../../utils/currency";
import { formatDateTime } from "../../utils/time";
import { Button } from "../common/Button";
import { SectionHeader } from "../common/SectionHeader";
import { Text } from "../common/Text";

type MetricKey = "costUsd" | "totalTokens";
type ChartMode = "bars" | "area" | "cumulative";

interface TooltipState {
  index: number;
  x: number;
  y: number;
}

export interface UsageTimeseriesChartProps {
  buckets: UsageTimeseriesBucket[] | undefined;
}

const BarsModeIcon: FC = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const AreaModeIcon: FC = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
    />
  </svg>
);

const CumulativeModeIcon: FC = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
    />
  </svg>
);

function getBucketValue(
  bucket: UsageTimeseriesBucket,
  metric: MetricKey,
): number {
  if (metric === "totalTokens") {
    return bucket.totals.totalTokens;
  }
  const candidate = bucket.totals.costUsd;
  return isFiniteNumber(candidate) ? candidate : 0;
}

function resolveThemeColor(type: "success" | "info", isDark: boolean): string {
  if (type === "success") {
    return isDark ? DARK_THEME.status.success : LIGHT_THEME.status.success;
  }
  return isDark ? DARK_THEME.status.info : LIGHT_THEME.status.info;
}

function getThemeColor(type: "success" | "info"): {
  solid: string;
  light: string;
} {
  const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const baseColor = resolveThemeColor(type, isDark);

  const hex = baseColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return {
    light: `rgba(${r},${g},${b},0.15)`,
    solid: `rgba(${r},${g},${b},0.7)`,
  };
}

function formatYAxisValue(value: number, metric: MetricKey): string {
  if (metric === "totalTokens") {
    return formatTokenCount(value);
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}k`;
  }
  if (value >= 1) {
    return `$${value.toFixed(0)}`;
  }
  return formatUsd(value);
}

/**
 * Generate a smooth monotone cubic spline path through points.
 * Uses Fritsch-Carlson method to ensure monotonicity (no overshooting).
 */
function generateSmoothPath(
  points: { x: number; y: number }[],
): { linePath: string; areaPath: string } | null {
  if (points.length < 2) {
    return null;
  }

  if (points.length === 2) {
    const linePath = `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
    const areaPath = `${linePath} L ${points[1].x} 100 L ${points[0].x} 100 Z`;
    return { areaPath, linePath };
  }

  // Calculate tangents using finite differences
  const n = points.length;
  const dx: number[] = [];
  const dy: number[] = [];
  const m: number[] = []; // Slopes

  for (let i = 0; i < n - 1; i++) {
    dx.push(points[i + 1].x - points[i].x);
    dy.push(points[i + 1].y - points[i].y);
    m.push(dx[i] === 0 ? 0 : dy[i] / dx[i]);
  }

  // Calculate tangent slopes at each point
  const tangents: number[] = [];
  tangents.push(m[0]);

  for (let i = 1; i < n - 1; i++) {
    if (m[i - 1] * m[i] <= 0) {
      // Sign change or zero - set tangent to 0 for monotonicity
      tangents.push(0);
    } else {
      // Harmonic mean of slopes (Fritsch-Carlson)
      tangents.push((2 * m[i - 1] * m[i]) / (m[i - 1] + m[i]) || 0);
    }
  }

  tangents.push(m[n - 2]);

  // Build the SVG path using cubic Bezier curves
  let linePath = `M ${points[0].x} ${points[0].y}`;

  for (let i = 0; i < n - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const segmentDx = dx[i];

    // Control points for cubic Bezier
    const cp1x = p0.x + segmentDx / 3;
    const cp1y = p0.y + (tangents[i] * segmentDx) / 3;
    const cp2x = p1.x - segmentDx / 3;
    const cp2y = p1.y - (tangents[i + 1] * segmentDx) / 3;

    linePath += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p1.x} ${p1.y}`;
  }

  // Close for area fill
  const lastPoint = points[n - 1];
  const [firstPoint] = points;
  const areaPath = `${linePath} L ${lastPoint.x} 100 L ${firstPoint.x} 100 Z`;

  return { areaPath, linePath };
}

const UsageChartGradient: FC<{
  colors: ReturnType<typeof getThemeColor>;
  metric: MetricKey;
}> = ({ colors, metric }) => (
  <defs>
    <linearGradient id={`gradient-${metric}`} x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stopColor={colors.solid} stopOpacity={0.3} />
      <stop offset="100%" stopColor={colors.light} stopOpacity={0} />
    </linearGradient>
  </defs>
);

const AreaChartOverlay: FC<{
  colors: ReturnType<typeof getThemeColor>;
  metric: MetricKey;
  paths: NonNullable<ReturnType<typeof generateSmoothPath>>;
  points: { x: number; y: number }[];
  tooltip: TooltipState | null;
}> = ({ colors, metric, paths, points, tooltip }) => {
  const hoveredPoint = tooltip ? points[tooltip.index] : null;

  return (
    <>
      <path d={paths.areaPath} fill={`url(#gradient-${metric})`} />
      <path
        d={paths.linePath}
        fill="none"
        stroke={colors.solid}
        strokeWidth={0.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        style={{ strokeWidth: 2 }}
      />
      {hoveredPoint && (
        <>
          <line
            x1={hoveredPoint.x}
            y1={0}
            x2={hoveredPoint.x}
            y2={100}
            stroke={colors.solid}
            strokeWidth={0.3}
            strokeDasharray="2 2"
          />
          <circle
            cx={hoveredPoint.x}
            cy={hoveredPoint.y}
            r={1.5}
            fill="white"
            stroke={colors.solid}
            strokeWidth={0.5}
          />
        </>
      )}
    </>
  );
};

export const UsageTimeseriesChart: FC<UsageTimeseriesChartProps> = ({
  buckets,
}) => {
  const [metric, setMetric] = useState<MetricKey>("costUsd");
  const [chartMode, setChartMode] = useState<ChartMode>("area");
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const data = useMemo(() => {
    const normalized = (buckets ?? []).slice(-120);
    const values = normalized.map((bucket) => getBucketValue(bucket, metric));
    const cumulativeValues = values.reduce<number[]>((acc, val) => {
      const prev = acc.length > 0 ? acc[acc.length - 1] : 0;
      acc.push(prev + val);
      return acc;
    }, []);
    const displayValues =
      chartMode === "cumulative" ? cumulativeValues : values;
    const max = Math.max(0, ...displayValues);
    return { cumulativeValues, displayValues, max, normalized, values };
  }, [buckets, metric, chartMode]);

  const colors = useMemo(
    () => getThemeColor(metric === "costUsd" ? "success" : "info"),
    [metric],
  );

  const yAxisLabels = useMemo(() => {
    if (data.max === 0) {
      return [];
    }
    const steps = [0, 0.25, 0.5, 0.75, 1];
    return steps.map((step) => ({
      label: formatYAxisValue(data.max * step, metric),
      value: data.max * step,
      y: 100 - step * 100,
    }));
  }, [data.max, metric]);

  const points = useMemo(() => {
    if (data.normalized.length === 0 || data.max === 0) {
      return [];
    }
    return data.displayValues.map((value, index) => {
      const x = (index / Math.max(1, data.normalized.length - 1)) * 100;
      const y = 100 - (value / data.max) * 95;
      return { x, y };
    });
  }, [data.normalized.length, data.displayValues, data.max]);

  const paths = useMemo(() => {
    if (chartMode === "bars" || points.length === 0) {
      return null;
    }
    return generateSmoothPath(points);
  }, [chartMode, points]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      const svg = event.currentTarget;
      const rect = svg.getBoundingClientRect();
      const relativeX = (event.clientX - rect.left) / rect.width;

      // Find closest point
      const index = Math.round(relativeX * (data.normalized.length - 1));
      const clampedIndex = Math.max(
        0,
        Math.min(data.normalized.length - 1, index),
      );

      // Calculate tooltip position in pixels relative to chart container
      const chartContainer = chartContainerRef.current;
      if (chartContainer) {
        const containerRect = chartContainer.getBoundingClientRect();
        const x = event.clientX - containerRect.left;
        const y = event.clientY - containerRect.top;
        setTooltip({ index: clampedIndex, x, y });
      }
    },
    [data.normalized.length],
  );

  const handleMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  if (!buckets || buckets.length === 0) {
    return (
      <div className="space-y-3">
        <SectionHeader title="Timeseries" />
        <div className="rounded-none border border-slate-300 bg-white p-4 text-sm text-gray-500 dark:border-slate-700 dark:bg-arti-panel dark:text-white/60">
          No timeseries buckets available for the selected range.
        </div>
      </div>
    );
  }

  const [first] = data.normalized;
  const last = data.normalized[data.normalized.length - 1];

  const tooltipBucket = tooltip ? data.normalized[tooltip.index] : null;
  const tooltipValue = tooltip ? data.displayValues[tooltip.index] : null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionHeader title="Timeseries" />
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-none border border-slate-300 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-arti-background">
            <Button
              variant={metric === "costUsd" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setMetric("costUsd")}
            >
              Cost
            </Button>
            <Button
              variant={metric === "totalTokens" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setMetric("totalTokens")}
            >
              Tokens
            </Button>
          </div>
          <div className="h-4 w-px bg-gray-200 dark:bg-white/10" />
          <div className="flex items-center rounded-none border border-slate-300 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-arti-background">
            <Button
              variant={chartMode === "bars" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setChartMode("bars")}
              className="!px-2"
              title="Bar chart"
            >
              <BarsModeIcon />
            </Button>
            <Button
              variant={chartMode === "area" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setChartMode("area")}
              className="!px-2"
              title="Area chart"
            >
              <AreaModeIcon />
            </Button>
            <Button
              variant={chartMode === "cumulative" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setChartMode("cumulative")}
              className="!px-2"
              title="Cumulative"
            >
              <CumulativeModeIcon />
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-none border border-slate-300 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-arti-panel">
        {/* Date range header */}
        <div className="flex items-center justify-between gap-3 text-xs text-gray-500 dark:text-white/60">
          <span>{formatDateTime(first.startTimestampMs)}</span>
          <span>{formatDateTime(last.endTimestampMs)}</span>
        </div>

        {/* Chart container */}
        <div ref={chartContainerRef} className="relative mt-3 h-36 w-full">
          {/* Y-axis labels */}
          <div className="absolute -left-1 top-0 flex h-full w-12 flex-col justify-between text-right">
            {yAxisLabels
              .slice()
              .toReversed()
              .map((label, idx) => (
                <span
                  key={idx}
                  className="text-xs text-gray-400 dark:text-white/40"
                >
                  {label.label}
                </span>
              ))}
          </div>

          {/* Chart area */}
          <div className="ml-12 h-full">
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="h-full w-full"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <UsageChartGradient colors={colors} metric={metric} />

              {/* Horizontal grid lines */}
              {[0, 25, 50, 75, 100].map((y) => (
                <line
                  key={y}
                  x1="0"
                  y1={y}
                  x2="100"
                  y2={y}
                  stroke="currentColor"
                  strokeWidth={0.2}
                  className="text-gray-200 dark:text-white/10"
                />
              ))}

              {chartMode === "bars"
                ? // Bar chart
                  data.normalized.map((bucket, index) => {
                    const value = data.displayValues[index] ?? 0;
                    const height = data.max > 0 ? (value / data.max) * 95 : 0;
                    const x =
                      (index / Math.max(1, data.normalized.length)) * 100;
                    const w = 100 / Math.max(1, data.normalized.length);
                    const y = 100 - height;
                    const isHovered = tooltip?.index === index;

                    return (
                      <rect
                        key={`${bucket.startTimestampMs}-${bucket.endTimestampMs}`}
                        x={x}
                        y={y}
                        width={Math.max(0.35, w - 0.3)}
                        height={height}
                        rx={0.3}
                        fill={isHovered ? colors.solid : colors.light}
                        stroke={colors.solid}
                        strokeWidth={isHovered ? 0.5 : 0.2}
                        className="transition-colors"
                      />
                    );
                  })
                : paths && (
                    <AreaChartOverlay
                      colors={colors}
                      metric={metric}
                      paths={paths}
                      points={points}
                      tooltip={tooltip}
                    />
                  )}
            </svg>
          </div>

          {/* Floating Tooltip */}
          {tooltip && tooltipBucket && tooltipValue !== null && (
            <div
              className="pointer-events-none absolute z-10 rounded-none border border-slate-300 bg-white px-3 py-2 text-xs shadow-sm dark:border-slate-700 dark:bg-gray-900"
              style={{
                left: Math.min(
                  tooltip.x + 12,
                  (chartContainerRef.current?.offsetWidth ?? 300) - 140,
                ),
                top: Math.max(tooltip.y - 50, 0),
                transform:
                  tooltip.x >
                  (chartContainerRef.current?.offsetWidth ?? 300) - 160
                    ? "translateX(-100%)"
                    : undefined,
              }}
            >
              <Text
                variant="label"
                as="div"
                className="mb-1 text-gray-900 dark:text-white"
              >
                {metric === "costUsd"
                  ? formatUsd(tooltipValue)
                  : formatTokenCount(tooltipValue)}
                {chartMode === "cumulative" && (
                  <span className="ml-1 text-gray-500 dark:text-white/50">
                    (cumulative)
                  </span>
                )}
              </Text>
              <div className="text-gray-500 dark:text-white/60">
                {formatDateTime(tooltipBucket.startTimestampMs)}
              </div>
            </div>
          )}
        </div>

        {/* Summary stats */}
        <div className="mt-3 flex items-center justify-center gap-4 border-t border-gray-100 pt-2.5 text-xs dark:border-white/5">
          <div className="text-center">
            <span className="text-gray-500 dark:text-white/50">Total</span>
            <span className="ml-1.5 font-mono text-[11px] font-bold text-slate-900 dark:text-white">
              {metric === "costUsd"
                ? formatUsd(data.values.reduce((a, b) => a + b, 0))
                : formatTokenCount(data.values.reduce((a, b) => a + b, 0))}
            </span>
          </div>
          <div className="h-3 w-px bg-gray-200 dark:bg-white/10" />
          <div className="text-center">
            <span className="text-gray-500 dark:text-white/50">Avg/day</span>
            <span className="ml-1.5 font-mono text-[11px] font-bold text-slate-900 dark:text-white">
              {metric === "costUsd"
                ? formatUsd(
                    data.values.length > 0
                      ? data.values.reduce((a, b) => a + b, 0) /
                          data.values.length
                      : 0,
                  )
                : formatTokenCount(
                    data.values.length > 0
                      ? data.values.reduce((a, b) => a + b, 0) /
                          data.values.length
                      : 0,
                  )}
            </span>
          </div>
          <div className="h-3 w-px bg-gray-200 dark:bg-white/10" />
          <div className="text-center">
            <span className="text-gray-500 dark:text-white/50">Peak</span>
            <span className="ml-1.5 font-mono text-[11px] font-bold text-slate-900 dark:text-white">
              {metric === "costUsd"
                ? formatUsd(Math.max(0, ...data.values))
                : formatTokenCount(Math.max(0, ...data.values))}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
